/**
 * EmojiPicker - Compact emoji selector for category icons
 * 
 * Provides a grid of commonly used emojis organized by category
 * with proper scroll isolation and icon-only sidebar navigation.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Close icon as inline SVG
const CloseIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

interface EmojiPickerProps {
  value?: string;
  onChange: (emoji: string) => void;
  onClose: () => void;
}

// Emojis organized by category - focused on finance/budget categories
const EMOJI_CATEGORIES = {
  money: {
    label: 'Argent',
    icon: '💰',
    emojis: ['💰', '💵', '💶', '💷', '💴', '💳', '🏦', '🏧', '💎', '📈', '📉', '💹', '🪙', '💸', '🤑', '💲']
  },
  food: {
    label: 'Alimentation',
    icon: '🛒',
    emojis: ['🛒', '🍞', '🥖', '🥐', '🧀', '🥩', '🍗', '🥚', '🥬', '🥕', '🍎', '🍊', '🥛', '🧈', '🫒', '🍯']
  },
  restaurant: {
    label: 'Restauration',
    icon: '🍽️',
    emojis: ['🍽️', '🍕', '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🍜', '🍝', '🍣', '🍱', '🥡', '🍛', '🥘', '🫕']
  },
  drinks: {
    label: 'Boissons',
    icon: '☕',
    emojis: ['☕', '🍵', '🧃', '🥤', '🍺', '🍻', '🍷', '🍸', '🍹', '🧋', '🥂', '🍾', '🫖', '🥃', '🧉', '🍶']
  },
  transport: {
    label: 'Transport',
    icon: '🚗',
    emojis: ['🚗', '🚙', '🚕', '🚌', '🚎', '🚇', '🚆', '🚊', '✈️', '⛽', '🚲', '🛵', '🏍️', '🚁', '🚢', '🛴', '🚐', '🛻', '🚚', '🚃']
  },
  home: {
    label: 'Logement',
    icon: '🏠',
    emojis: ['🏠', '🏡', '🏢', '🔑', '🛋️', '🛏️', '🚿', '🛁', '🔧', '🔨', '💡', '🌡️', '🧹', '🪴', '🪑', '🚪', '🪟', '🏗️', '🧰', '🪠']
  },
  health: {
    label: 'Santé',
    icon: '🏥',
    emojis: ['🏥', '💊', '💉', '🩺', '🩹', '🧬', '🦷', '👓', '🧘', '🏃', '💪', '❤️', '🩻', '🩼', '🧑‍⚕️', '🏋️', '🚴', '🧖', '💆', '🩸']
  },
  entertainment: {
    label: 'Loisirs',
    icon: '🎬',
    emojis: ['🎬', '🎮', '🎵', '🎸', '📺', '📚', '🎨', '🎭', '🎪', '🎯', '🎳', '🎰', '🎲', '🧩', '♠️', '🎻']
  },
  sports: {
    label: 'Sports',
    icon: '⚽',
    emojis: ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏓', '🏸', '🏊', '⛷️', '🎿', '🏕️', '⛳', '🏌️', '🥊', '🚣', '🏄', '🧗', '🤿', '🎣']
  },
  shopping: {
    label: 'Shopping',
    icon: '🛍️',
    emojis: ['👗', '👔', '👕', '👖', '👟', '👠', '👜', '🎒', '💄', '💅', '🎁', '🛍️', '👒', '🧥', '👙', '🩱']
  },
  tech: {
    label: 'Tech',
    icon: '💻',
    emojis: ['📱', '📲', '☎️', '📞', '💻', '🖥️', '🖨️', '⌨️', '🖱️', '📷', '📹', '🎥', '📡', '📺', '🔌', '💾', '📀', '🎧', '🎤', '🔋']
  },
  subscriptions: {
    label: 'Abonnements',
    icon: '📶',
    emojis: ['📱', '📶', '📡', '🌐', '📺', '🎬', '🎵', '🎮', '📰', '📧', '☁️', '🔐', '📦', '🏋️', '📚', '🎧']
  },
  education: {
    label: 'Éducation',
    icon: '🎓',
    emojis: ['📖', '📚', '🎓', '✏️', '📝', '🖊️', '📐', '🔬', '🔭', '🧪', '🎒', '🏫', '👨‍🎓', '👩‍🏫', '📓', '📒', '🧮', '🗃️', '📔', '📕']
  },
  work: {
    label: 'Travail',
    icon: '💼',
    emojis: ['💼', '📊', '📋', '📁', '📂', '🗂️', '📎', '✂️', '📌', '🖨️', '📧', '💻', '🏢', '👔', '📅', '📆', '🗓️', '📇', '🗄️', '📤']
  },
  family: {
    label: 'Famille',
    icon: '👨‍👩‍👧',
    emojis: ['👶', '👧', '👦', '🧒', '👨‍👩‍👧', '👨‍👩‍👦', '👪', '🏫', '🎂', '🎄', '🎃', '🐕', '🐈', '🎁', '🍼', '🧸', '🎠', '🎡', '🐾', '🐇']
  },
  services: {
    label: 'Services',
    icon: '⚡',
    emojis: ['📞', '☎️', '📡', '🌐', '📬', '📮', '🔒', '⚡', '💧', '🔥', '♻️', '🧾', '📑', '🗑️', '🚰', '⛽', '🛠️', '🧯', '🪪', '📋']
  },
  travel: {
    label: 'Voyages',
    icon: '✈️',
    emojis: ['✈️', '🏨', '🏖️', '🏝️', '🗼', '🗽', '🏰', '⛺', '🧳', '🗺️', '🌍', '🌎', '🌏', '🚢', '🛳️', '🎢', '🏜️', '🌅', '🌄', '🏔️']
  },
  pets: {
    label: 'Animaux',
    icon: '🐾',
    emojis: ['🐕', '🐈', '🐦', '🐹', '🐰', '🐠', '🐢', '🐍', '🦜', '🐾', '🦴', '🥣', '🏥', '✂️', '🦮', '🐩', '🐕‍🦺', '🪺', '🐿️', '🦔']
  },
  other: {
    label: 'Divers',
    icon: '⭐',
    emojis: ['⭐', '🌟', '✨', '🔔', '📢', '🏷️', '🎯', '🚀', '⚙️', '🔄', '❓', '❗', '✅', '❌', '⚠️', '📍', '🔗', '💬', '🗨️', '📌']
  }
};

export default function EmojiPicker({ value, onChange, onClose }: EmojiPickerProps) {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('money');
  const sidebarRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Prevent scroll propagation to parent
  const preventScrollPropagation = useCallback((e: WheelEvent) => {
    const target = e.currentTarget as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
      e.preventDefault();
    }
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    const grid = gridRef.current;

    if (sidebar) {
      sidebar.addEventListener('wheel', preventScrollPropagation, { passive: false });
    }
    if (grid) {
      grid.addEventListener('wheel', preventScrollPropagation, { passive: false });
    }

    return () => {
      if (sidebar) {
        sidebar.removeEventListener('wheel', preventScrollPropagation);
      }
      if (grid) {
        grid.removeEventListener('wheel', preventScrollPropagation);
      }
    };
  }, [preventScrollPropagation]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSelect = (emoji: string) => {
    onChange(emoji);
    onClose();
  };

  const categories = Object.entries(EMOJI_CATEGORIES);
  const currentCategory = EMOJI_CATEGORIES[activeCategory as keyof typeof EMOJI_CATEGORIES];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl border border-slate-200 dark:border-slate-700 w-72 overflow-hidden">
      {/* Header with close button */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wide">
          {currentCategory.label}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        >
          <CloseIcon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        </button>
      </div>

      {/* Category tabs - horizontal scrollable */}
      <div 
        ref={sidebarRef}
        className="flex gap-1 p-2 overflow-x-auto border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
        style={{ overscrollBehavior: 'contain' }}
      >
        {categories.map(([key, cat]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveCategory(key)}
            title={cat.label}
            className={`flex-shrink-0 w-8 h-8 flex items-center justify-center text-lg rounded transition-colors ${
              activeCategory === key
                ? 'bg-violet-100 dark:bg-violet-900/50 ring-2 ring-violet-500'
                : 'hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {cat.icon}
          </button>
        ))}
      </div>

      {/* Emoji grid */}
      <div 
        ref={gridRef}
        className="p-2 overflow-y-auto"
        style={{ height: '200px', overscrollBehavior: 'contain' }}
      >
        <div className="grid grid-cols-6 gap-1">
          {currentCategory.emojis.map((emoji, index) => (
            <button
              key={`${emoji}-${index}`}
              type="button"
              onClick={() => handleSelect(emoji)}
              className={`w-9 h-9 text-xl flex items-center justify-center rounded hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-all hover:scale-110 ${
                value === emoji 
                  ? 'bg-violet-100 dark:bg-violet-900/50 ring-2 ring-violet-500' 
                  : ''
              }`}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
        <button
          type="button"
          onClick={() => handleSelect('')}
          className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        >
          {t('categories.modal.clearIcon', 'Aucune icône')}
        </button>
        {value && (
          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <span>Sélection:</span>
            <span className="text-xl">{value}</span>
          </div>
        )}
      </div>
    </div>
  );
}
